/**
 * Where a provider set is actually used — the "Used by" column on the tiered
 * and parallel set pages.
 *
 * "Active" means the configs app settings actually wires in: the default
 * pipeline config, plus the re-eval and eval-judge ones when they name a
 * different config. Every other stored config is reachable only by a per-user
 * override, so counting it would make a set look load-bearing when nothing
 * routes through it.
 *
 * A tiered set is referenced by `run.providerSet`. A parallel set is referenced
 * two ways: directly by `run.parallelSet`, and indirectly by a tiered set whose
 * tier slot points at it — a set that only appears via a tier is still in use,
 * and deleting it would break that ladder.
 */
import type { AppSettings, OperationsMap, ProviderTieredSet } from '../../types/settings';

export interface SetUsage {
    /** Operation key — 'analysis', 'hint', … */
    operation: string;
    /** The pipeline instance's human label within that operation. */
    pipelineName: string;
    /** Pipeline config doc this came from. */
    configName: string;
    /** False when the pipeline is switched off — still a reference, but nothing
     *  runs through it today. */
    enabled: boolean;
    /** Set when the reference is via a tiered set's tier slot rather than the
     *  cell itself. Carries the tiered set's name. */
    viaTieredSet?: string;
}

/** The configs a `Used by` column should scan. Deduped, order-stable. */
export function activeConfigNames(settings?: AppSettings): string[] {
    const names = [
        settings?.defaultPipelineConfig || 'default',
        settings?.reEvalPipelineConfig,
        settings?.evalJudgeConfig,
    ].filter((n): n is string => !!n);
    return [...new Set(names)];
}

interface ConfigOperations {
    name: string;
    operations?: OperationsMap;
}

/**
 * Build `set name → where it is used`, for both kinds at once.
 *
 * Walking every config once and bucketing by name beats a per-set scan: the
 * pages render one row per set, and a scan per row would re-walk the whole
 * config tree for each.
 */
export function buildSetUsage(
    configs: ConfigOperations[],
    tieredSets: ProviderTieredSet[],
): { tiered: Map<string, SetUsage[]>; parallel: Map<string, SetUsage[]> } {
    const tiered = new Map<string, SetUsage[]>();
    const parallel = new Map<string, SetUsage[]>();
    const add = (map: Map<string, SetUsage[]>, key: string, usage: SetUsage) => {
        const list = map.get(key) ?? [];
        // One pipeline can reference the same set from several steps/runs; the
        // column answers "which pipelines", so collapse those to one entry.
        if (list.some(u => u.configName === usage.configName
            && u.operation === usage.operation
            && u.pipelineName === usage.pipelineName
            && u.viaTieredSet === usage.viaTieredSet)) return;
        list.push(usage);
        map.set(key, list);
    };

    // Tier slots pointing at a parallel set, so a direct hit on a tiered set can
    // be attributed to that parallel set too.
    const parallelsOfTieredSet = new Map<string, string[]>();
    for (const set of tieredSets) {
        const names = (set.levels ?? []).flatMap(slot =>
            slot?.kind === 'parallel' && slot.parallelSet ? [slot.parallelSet] : []);
        if (names.length) parallelsOfTieredSet.set(set.name, [...new Set(names)]);
    }

    for (const config of configs) {
        for (const [operation, opConfig] of Object.entries(config.operations ?? {})) {
            for (const pipeline of opConfig?.pipelines ?? []) {
                const base = {
                    operation,
                    pipelineName: pipeline.pipelineName,
                    configName: config.name,
                    enabled: pipeline.enabled !== false,
                };
                for (const step of pipeline.steps ?? []) {
                    for (const run of step.runs ?? []) {
                        if (run.providerSet) {
                            add(tiered, run.providerSet, base);
                            for (const pName of parallelsOfTieredSet.get(run.providerSet) ?? []) {
                                add(parallel, pName, { ...base, viaTieredSet: run.providerSet });
                            }
                        }
                        if (run.parallelSet) add(parallel, run.parallelSet, base);
                    }
                }
            }
        }
    }

    return { tiered, parallel };
}
